import axios from 'axios';

// AirNow API key from .env (frontend/.env locally) **only** when using the CORS
// proxy in development.  In production we do not read or embed the key at all –
// the request is routed through a Netlify Function which has access to the
// secret `AIRNOW_API_KEY` environment variable.  The presence of
// `REACT_APP_AIRNOW_API_KEY` here is strictly for local debugging with
// `REACT_APP_USE_CORS_PROXY=true` and will be tree‑shaken away in production.
// Return the AirNow API key that the *client* is allowed to use.  In
// normal production we do **not** expose the key to the browser at all –
// the request goes through our Netlify Function which reads the secret
// from process.env.AIRNOW_API_KEY.  The only time the frontend needs the
// key is when you're running locally without the function and falling
// back to the public CORS proxy; in that case set
// `REACT_APP_USE_CORS_PROXY=true` and `REACT_APP_AIRNOW_API_KEY` in your
// local `.env` file.  The build-time DEFINE_PLUGIN will then inline the
// key, but because `REACT_APP_USE_CORS_PROXY` will be `false` in
// production the minifier can drop the dead code and the key won't end up
// in the bundle.
function getAirNowApiKey() {
  if (process.env.REACT_APP_USE_CORS_PROXY !== 'true') return '';
  const raw = process.env.REACT_APP_AIRNOW_API_KEY;
  if (raw === undefined || raw === null) return '';
  const s = String(raw).trim();
  return s.length > 0 ? s : '';
}

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

// EPA breakpoints: PM2.5 µg/m³ → US AQI
function pm25ToUsAqi(pm25) {
  if (pm25 == null || !Number.isFinite(pm25)) return null;
  const breakpoints = [
    { cLow: 0.0, cHigh: 12.0, iLow: 0, iHigh: 50 },
    { cLow: 12.1, cHigh: 35.4, iLow: 51, iHigh: 100 },
    { cLow: 35.5, cHigh: 55.4, iLow: 101, iHigh: 150 },
    { cLow: 55.5, cHigh: 150.4, iLow: 151, iHigh: 200 },
    { cLow: 150.5, cHigh: 250.4, iLow: 201, iHigh: 300 },
    { cLow: 250.5, cHigh: 350.4, iLow: 301, iHigh: 400 },
    { cLow: 350.5, cHigh: 500.4, iLow: 401, iHigh: 500 },
  ];
  const bp = breakpoints.find((b) => pm25 >= b.cLow && pm25 <= b.cHigh);
  if (!bp) return 500;
  const aqi = ((bp.iHigh - bp.iLow) / (bp.cHigh - bp.cLow)) * (pm25 - bp.cLow) + bp.iLow;
  return Math.round(aqi);
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Station weight: closer and fresher = higher (0..1)
function stationWeight(distanceKm, ageHours) {
  const dist = Number.isFinite(distanceKm) ? distanceKm : 25;
  const age = Number.isFinite(ageHours) ? ageHours : 6;
  const dWeight = Math.max(0, 1 - dist / 50);
  const tWeight = Math.max(0, 1 - age / 24);
  return (dWeight * 0.6 + tWeight * 0.4);
}

/**
 * Fetch model AQI + PM2.5 from Open-Meteo (fallback when no station data).
 */
async function fetchOpenMeteoAqi(lat, lon) {
  const url = `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lon}&current=us_aqi,pm2_5`;
  const { data } = await axios.get(url);
  const current = data?.current;
  if (!current) return null;
  return {
    aqi: current.us_aqi != null ? current.us_aqi : null,
    pm25: current.pm2_5 != null ? current.pm2_5 : null,
  };
}

/**
 * Fetch current AQI from EPA AirNow (official US monitors). The request is
 * routed through our Netlify Function which attaches the API key server-side.
 * (The client only needs the key when falling back to the public CORS proxy
 * during local development.)
 * Returns the max AQI across pollutants (EPA convention). Values align with
 * Apple Weather / Ventusky.
 */
async function fetchAirNowAqi(lat, lon) {
  // The AirNow API doesn't support browser CORS, which is why the old
  // implementation attempted to go through `corsproxy.io`.  That proxy
  // frequently returns 403 (as you've seen), and public proxies are both
  // unreliable and expose your key.  Instead we run a simple Netlify
  // function on the server that attaches the key and forwards the request.

  // On Netlify you'll need to define the same key as an environment variable
  // (either AIRNOW_API_KEY or REACT_APP_AIRNOW_API_KEY); the function reads
  // from those names.  When developing locally you can run `netlify dev` to
  // have the function available at the same origin, or continue using the
  // proxy by setting REACT_APP_USE_CORS_PROXY in your .env.

  // choose endpoint: first try our own serverless proxy, otherwise fall back
  // to the old corsproxy for local/dev setups that don't run Netlify dev.
  let url;
  const usingFunction =
    window.location.hostname.endsWith('netlify.app') ||
    process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_USE_CORS_PROXY !== 'true';
  if (usingFunction) {
    // call the Netlify Function (same origin, no CORS required).  we don't
    // need the API key in the browser at all for this path.
    url = `/ .netlify/functions/airnow?lat=${lat}&lon=${lon}`.replace(/\s+/g, '');
  } else {
    // using the public proxy; we must have a key to send with the request.
    const apiKey = getAirNowApiKey();
    if (!apiKey) return null;
    const params =
      `latitude=${lat}&longitude=${lon}&API_KEY=${encodeURIComponent(apiKey)}` +
      `&format=application/json&distance=25`;
    const endpoints = [
      `https://www.airnowapi.org/aq/observation/latLong/current/?${params}`,
      `https://www.airnowapi.org/CurrentObservationsByLatLon/query?${params}`,
    ];
    url = `https://corsproxy.io/?url=${encodeURIComponent(endpoints[0])}`;
  }

  try {
    const { data } = await axios.get(url, { timeout: 12000 });
    if (!Array.isArray(data) || data.length === 0) {
      console.warn('[AirNow] no observations returned');
      return null;
    }
    const aqis = data.map((ob) => ob.AQI).filter((a) => a != null && Number.isFinite(a));
    if (aqis.length === 0) return null;
    return { aqi: Math.max(...aqis) };
  } catch (err) {
    console.warn('[AirNow] fetch failed', err);
    return null;
  }
}

/**
 * Fetch nearest station PM2.5 from OpenAQ (official monitors). DISABLED: OpenAQ v3 requires API key and blocks CORS from browser (401). Kept for reference.
 */
async function fetchOpenAQNearest(_lat, _lon) {
  return null;
}

/**
 * Traffic proxy: road density from Overpass (OSM). Returns 0..1.
 */
async function fetchTrafficScore(lat, lon) {
  const radiusM = 1500;
  try {
    const query = `[out:json][timeout:8];(way["highway"~"^(motorway|trunk|primary|secondary|tertiary|residential)$"](around:${radiusM},${lat},${lon}););out count;`;
    const { data } = await axios.post(
      'https://overpass-api.de/api/interpreter',
      query,
      { headers: { 'Content-Type': 'text/plain' }, timeout: 10000 }
    );
    const count = data?.elements?.length ?? 0;
    // Normalize to ~0..1 (e.g. 0 roads = 0, many roads = cap at 1)
    const score = clamp(count / 80, 0, 1);
    return score;
  } catch (e) {
    return 0;
  }
}

/**
 * Smoke/fire proxy: heuristic from elevated PM2.5 (no external fire API to avoid CORS/key). 0..1.
 */
function smokeRiskFromPm25(pm25) {
  if (pm25 == null || !Number.isFinite(pm25)) return 0;
  // PM2.5 > 35 µg/m³ often indicates smoke or heavy pollution; scale to 0..1
  return clamp((pm25 - 12) / 80, 0, 1);
}

/**
 * Fuse AQI from multiple sources. Prefer EPA AirNow (when key set); fall back to Open-Meteo. OpenAQ not used (v3 requires key + CORS blocks browser).
 * @param {number} lat
 * @param {number} lon
 * @returns {Promise<{ aqi: number | null, components?: object }>}
 */
export async function getFusedAqi(lat, lon) {
  const latNum = parseFloat(lat);
  const lonNum = parseFloat(lon);
  if (!Number.isFinite(latNum) || !Number.isFinite(lonNum)) {
    return { aqi: null };
  }

  const [airNow, openMeteo, trafficScore] = await Promise.all([
    fetchAirNowAqi(latNum, lonNum),
    fetchOpenMeteoAqi(latNum, lonNum),
    fetchTrafficScore(latNum, lonNum),
  ]);

  const aqiModel = openMeteo?.aqi ?? null;

  // 1) Prefer EPA AirNow when available (matches Apple / Ventusky)
  if (airNow?.aqi != null) {
    const aqi = Math.round(clamp(airNow.aqi, 0, 500));
    return {
      aqi,
      components: {
        aqi_airnow: airNow.aqi,
        aqi_model: aqiModel,
        traffic_score: trafficScore,
        smoke_risk: openMeteo?.pm25 != null ? smokeRiskFromPm25(openMeteo.pm25) : 0,
      },
    };
  }

  // 2) Fall back to Open-Meteo (may read higher than Apple/Ventusky)
  if (aqiModel == null) return { aqi: null, components: {} };
  const aqi = Math.round(clamp(aqiModel, 0, 500));
  return {
    aqi,
    components: {
      aqi_model: aqiModel,
      traffic_score: trafficScore,
      smoke_risk: openMeteo?.pm25 != null ? smokeRiskFromPm25(openMeteo.pm25) : 0,
    },
  };
}
